import i18n, { isEnglish, formatDate } from "@/i18n";

// MBR116: the Home tier note and expiry notices arrive from crm.api as ready-built English
// (Helper::processLevelData / getNotification*Expiry). Re-render the known shapes through
// $t for other languages; English, or any text that stops matching, is shown as returned.
// Amounts, tier names and the merchant's point name are data and stay untranslated.

const t = (key, params) => i18n.global.t(key, params);

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// "Spend $ 10.00 more to become Gold" | "3 more attendances to Gold" | " You're Gold"
export function translateLevelNote(text) {
  if (!text || isEnglish()) {
    return text;
  }
  const note = String(text).trim();
  let match = note.match(/^Spend (.+?) more to become (.+)$/);
  if (match) {
    return t("home.level_spend_more", { amount: match[1], tier: match[2] });
  }
  match = note.match(/^(\d+) more attendances to (.+)$/);
  if (match) {
    return t("home.level_visits_more", { count: match[1], tier: match[2] });
  }
  match = note.match(/^You're (.+)$/);
  if (match) {
    return t("home.level_reached", { tier: match[1] });
  }
  return text;
}

// HTML: "(7 Point expiring in <span style="color:#f00">23 days,</span> 22/10/2026 18:00)",
// "1 day," for tomorrow, or "(7 Point expiring <span style="color:#f00">today,</span> ...)",
// with "cashback" / "topup" / "bonus" after the amount for wallet balances. The time is optional so
// notices from an API that still sends the date alone keep translating. Returns HTML for v-html.
export function translateExpiryNotice(html) {
  if (!html || isEnglish()) {
    return html;
  }
  const match = String(html).match(
    /^\((.+?) expiring (?:in <span[^>]*>(\d+) days?,|<span[^>]*>today,)<\/span>\s*(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?\)$/
  );
  if (!match) {
    return html;
  }
  const [, subject, days, dd, mm, yyyy, hh, mi] = match;
  const hasTime = hh !== undefined;
  const expiry = new Date(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    hasTime ? Number(hh) : 0,
    hasTime ? Number(mi) : 0
  );
  const date = formatDate(expiry, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(hasTime ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" } : {}),
  });
  const params = {
    days: `<span>${escapeHtml(t("home.expiry_days", { count: days }))}</span>`,
    date: escapeHtml(date),
  };
  let key = "home.expiry_point";
  let amount = subject;
  const wallet = subject.match(/^(.+) (cashback|topup|bonus)$/);
  if (wallet) {
    key = `home.expiry_${wallet[2]}`;
    amount = wallet[1];
  }
  if (days === undefined) {
    key += "_today";
  }
  params.amount = escapeHtml(amount);
  return t(key, params);
}

// MBR117: bundles carry a ready-built English count ("No voucher" / "1 voucher" / "3 vouchers")
// next to the number itself — a plain number in lists, { total, detail } on bundle detail.
export function voucherCountText(bundle) {
  const count = bundle?.number_of_vouchers;
  const n = Number(count !== null && typeof count === "object" ? count.total : count);
  if (count === undefined || count === null || Number.isNaN(n)) {
    return bundle?.number_of_vouchers_text;
  }
  if (n < 1) {
    return t("voucher.count_none");
  }
  return n === 1 ? t("voucher.count_one") : t("voucher.count_many", { n });
}

// MBR139: a dated voucher reads "Valid till <date>"; an "expired after" voucher's text is a
// duration ("30 day"), which keeps "Valid for". `group` is the locale block holding both keys.
export function validityText(dateText, group = "home") {
  const key = /^\d+ (day|month|year)$/.test(dateText ?? "") ? "valid_for" : "valid_till";
  return t(`${group}.${key}`, { date: dateText });
}
